import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import { red } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EmojiIcon from '@mui/icons-material/EmojiEmotions';
import CommentIcon from '@mui/icons-material/ModeComment';
import dynamic from 'next/dynamic';
import { withSnackbar } from 'notistack';
import React, { useEffect, useState, Fragment } from 'react';
import { VOTE } from '../../helpers/graphql/broadcast';
import graphQLClient from '../../helpers/graphQLClient';
import { getUser } from '../../helpers/token';
import Link from '@mui/material/Link';
import LoginButton from '../Header/LoginButton';
import BookmarkIcon from './BookmarkIcon';
import EmojiSlider from './EmojiSlider';
import VoteButton from './VoteButton';

const downVoteTheme = createTheme({
  palette: {
    primary: red,
  },
});

const CommentEditor = dynamic(() => import('../Editor/CommentEditor'), {
  loading: () => <LinearProgress />,
  ssr: false,
});

const VoteSlider = props => {
  const [content, setContent] = useState('');
  const [voteExpanded, setVoteExpanded] = useState(false);
  const [commentExpanded, setCommentExpanded] = useState(false);
  const [loading, setLoading] = useState(undefined);
  const [weight, setWeight] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [totalMiles, setTotalMiles] = useState(null);
  const [open, setOpen] = useState(false);
  const [downvoteConfirmOpen, setDownvoteConfirmOpen] = useState(false);

  const user = getUser();

  useEffect(() => {
    setTotalMiles(props.totalSmiles);
    if (props.smiles !== '' && props.smiles !== undefined) {
      props.smiles.forEach(({ voter, value }) => {
        if (voter === user) {
          setWeight(value);
          setHasVoted(true);
        }
      });
    }
  }, [props]);

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      props.enqueueSnackbar(notification.message, { variant });
    }
  };

  const pastVote = res => {
    if (!res.success) newNotification(res);
    else {
      setHasVoted(true);
      setTotalMiles(totalMiles + Math.round(weight));
    }
    setLoading(undefined);
    setVoteExpanded(false);
  };

  const votePost = () => {
    setLoading(0);
    const { author, permlink } = props;
    const variables = {
      author,
      permlink,
      weight: Math.round(weight * 1000),
    };
    graphQLClient(VOTE, variables)
      .then(res => {
        if (res && res.vote) pastVote(res.vote);
      })
      .catch(err => {
        newNotification({
          success: false,
          message:
            err.message === 'Failed to fetch'
              ? 'Network Error. Are you online?'
              : `Could not vote: ${err.message}`,
        });
        setLoading(undefined);
      });
  };

  const triggerVote = () => {
    if (weight === 0) setWeight(0.001);
    if (weight > 0) votePost();
    else setDownvoteConfirmOpen(true);
  };

  const confirmDownvote = () => {
    setDownvoteConfirmOpen(false);
    votePost();
  };

  const handleWeightChange = (event, value) => {
    setWeight(value);
  };

  const actions = [];
  const commentButton = (
    <div className="col p-0">
      <Typography color="textSecondary" component="span">
        <Button
          fullWidth
          onClick={
            user != null ? () => setCommentExpanded(true) : () => setOpen(true)
          }
          size="small"
          color="inherit"
        >
          {(props.mode === 'gridcard' && (
            <CommentIcon className="mr mr-1" />
          )) || <AddCommentIcon className="mr mr-1" />}
          {props.noOfComments > 0 &&
            props.mode !== 'sticky' &&
            !props.hideCommentNumber && (
              <Box
                className="pl-1 pr-1"
                fontSize={16}
                color="inherit"
                fontWeight="fontWeightBold"
                component="span"
              >
                {props.noOfComments}
              </Box>
            )}
          <Typography noWrap variant="inherit" className="p-1">
            {props.noOfComments > 1 &&
            props.mode !== 'sticky' &&
            !props.hideCommentNumber
              ? 'Comments'
              : 'Comment'}
          </Typography>
        </Button>
      </Typography>
    </div>
  );
  let cardFooter = <></>;
  actions.push(
    <div className="col p-0">
      <Typography color="textSecondary" component="span">
        <Button
          fullWidth
          onClick={
            user != null ? () => setVoteExpanded(true) : () => setOpen(true)
          }
          size="small"
          color={hasVoted ? 'primary' : 'inherit'}
        >
          {(hasVoted && <VoteButton weight={weight} size="24" />) || (
            <EmojiIcon />
          )}
          {props.mode !== 'sticky' && (
            <Box
              className="pl-2 pr-1"
              fontSize={16}
              color={hasVoted ? 'primary' : 'inherit'}
              fontWeight="fontWeightBold"
              component="span"
            >
              {totalMiles || props.totalSmiles}
            </Box>
          )}
          <Typography noWrap variant="inherit" className="p-1">
            Smiles
          </Typography>
        </Button>
      </Typography>
    </div>,
  );
  if (props.mode === 'gridcard' && user != null) {
    actions.push(
      <div className="col p-0">
        <Link
          color="textPrimary"
          href={`/@${props.author}/${props.permlink}#comments`}
        >
          {commentButton}
        </Link>
      </div>,
    );
  } else {
    actions.push(<>{commentButton}</>);
  }
  if (!props.hideSaveBtn)
    actions.push(
      <div className="col p-0 d-none d-xl-block d-lg-block d-md-block d-sm-block">
        <Typography color="textSecondary" component="span">
          <BookmarkIcon
            onBmChange={props.onBmChange}
            isButton
            author={props.author}
            permlink={props.permlink}
          />
        </Typography>
      </div>,
    );
  if (props.handleClick !== undefined) {
    if (props.isEdit === true) {
      actions.push(
        <div className="col p-0 d-none d-xl-block d-lg-block d-md-block d-sm-block">
          <Typography color="textSecondary">
            <Button
              fullWidth
              onClick={() => {
                props.handleClick();
              }}
              size="small"
              color="inherit"
            >
              <EditIcon className="mr pr-1 mr-1" />
              <Typography noWrap variant="inherit" className="p-1">
                Edit
              </Typography>
            </Button>
          </Typography>
        </div>,
      );
    }
  }
  if (voteExpanded === false) {
    cardFooter = (
      <>
        {!props.noDivider && <Divider variant="middle" />}
        <CardActions>
          <div className="container w-100">
            <div className="row">
              {actions.map((action, i) => {
                return <Fragment key={i}>{action}</Fragment>;
              })}
            </div>
          </div>
        </CardActions>
      </>
    );
  }
  if (voteExpanded === true) {
    cardFooter = (
      <>
        {!props.noDivider && <Divider variant="middle" />}
        <CardActions>
          <div className="container w-100" style={{ height: '38px' }}>
            <div className="row h-100">
              <div className="my-auto col-12 text-center">
                <EmojiSlider
                  loading={loading}
                  weight={weight}
                  setWeight={handleWeightChange}
                  onChangeCommitted={triggerVote}
                />
              </div>
            </div>
          </div>
          <IconButton
            size="small"
            onClick={() => setVoteExpanded(false)}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </CardActions>
      </>
    );
  }
  if (props.mode !== 'gridcard' && commentExpanded === true) {
    cardFooter = (
      <>
        <Divider variant="middle" />
        <CardActions>
          <div className="w-100">
            <CommentEditor
              defaultValue={content}
              setContent={setContent}
              parent_author={props.author}
              parent_permlink={props.permlink}
              onClose={() => {
                setCommentExpanded(false);
                setContent('');
              }}
              onCommentAdd={props.onCommentAdd}
              parentId={props.id}
              rootId={props.id}
              rootTitle={props.title}
              rootAuthor={props.author}
              rootPermlink={props.permlink}
              depth={1}
            />
          </div>
        </CardActions>
      </>
    );
  }
  return (
    <>
      {open && (
        <LoginButton
          open={open}
          hideButtons
          onClickClose={() => setOpen(false)}
          text=" to vote and reply to posts"
        />
      )}
      {cardFooter}
      <Dialog
        open={downvoteConfirmOpen}
        onClose={() => setDownvoteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Downvote?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure that you want to downvote this post? Downvoting removes
            rewards from this post.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownvoteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={downVoteTheme}>
              <Button
                variant="contained"
                onClick={confirmDownvote}
                color="primary"
              >
                Downvote
              </Button>
            </ThemeProvider>
          </StyledEngineProvider>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withSnackbar(VoteSlider);
